import { useState, useRef, useCallback, useEffect } from "react"
import { Chess } from "chess.js"
import { runPipeline, type PipelineResult } from "@client/engine/pipeline"
import type { StockfishProgress } from "@client/engine/stockfish"
import type { CreateReplayResponse, NarrateStreamEvent } from "@shared/types"
import styles from "./SenderPage.module.css"

// --- Types ---

type PageState = "input" | "processing" | "result" | "error"

type NudgeOption = "comeback" | "tactic" | "trap" | "blunder" | "positional" | null

interface ProcessingStage {
  id: string
  label: string
  status: "pending" | "active" | "complete" | "error"
  detail?: string
}

interface ResultData {
  id: string
  url: string
  headline: string
  hook: string
  white: string
  black: string
  result: string
}

interface ErrorData {
  title: string
  message: string
  canRetry: boolean
}

// --- Constants ---

const NUDGE_OPTIONS: { value: NudgeOption; label: string }[] = [
  { value: null, label: "Auto-detect" },
  { value: "comeback", label: "Comeback" },
  { value: "tactic", label: "Tactic" },
  { value: "trap", label: "Trap" },
  { value: "blunder", label: "Blunder" },
  { value: "positional", label: "Positional" },
]

const PGN_PLACEHOLDER = `Paste a PGN or a Chess.com / Lichess game link

https://www.chess.com/game/live/123456789
https://lichess.org/abcd1234

1. e4 e5 2. Nf3 Nc6 3. Bb5 a6 ...`

const INVITE_CODE_KEY = "kibitzshare_invite_code"
const REPLAY_COUNT_KEY = "kibitzshare_replay_count"
const FEEDBACK_DISMISSED_KEY = "kibitzshare_feedback_dismissed"
const FEEDBACK_FORM_URL = "" // TODO: replace with actual Google Form URL
const FEEDBACK_THRESHOLD = 2 // Show banner after this many replays

const MAX_PGN_LENGTH = 10_000

const STOCKFISH_TIMEOUT_MS = 60_000
const STOCKFISH_WARN_MS = 90_000

// --- Helpers ---

function validatePgn(pgn: string): { valid: boolean; error: string | null } {
  const trimmed = pgn.trim()
  if (!trimmed) {
    return { valid: false, error: null }
  }

  try {
    const chess = new Chess()
    chess.loadPgn(trimmed)
    const history = chess.history()
    if (history.length < 4) {
      return {
        valid: false,
        error: "This game is too short. Please paste a game with at least 2 full moves.",
      }
    }
    return { valid: true, error: null }
  } catch {
    return {
      valid: false,
      error: "This doesn't look like a valid PGN. Make sure you're pasting the full game notation.",
    }
  }
}

const GAME_URL_PATTERN =
  /(?:https?:\/\/)?(?:www\.)?(chess\.com\/game\/(?:live|daily)\/\d+|lichess\.org\/[a-zA-Z0-9]{8})/i

function looksLikeGameUrl(input: string): boolean {
  return GAME_URL_PATTERN.test(input.trim())
}

async function fetchPgnFromLink(url: string): Promise<{ ok: true; pgn: string } | { ok: false; error: string }> {
  const response = await fetch(`/api/import?url=${encodeURIComponent(url.trim())}`)
  const data = await response.json() as { ok: boolean; pgn?: string; error?: string }
  if (data.ok && data.pgn) {
    return { ok: true, pgn: data.pgn }
  }
  return { ok: false, error: data.error ?? "Could not fetch game from that link." }
}

const LICHESS_PERSPECTIVE_PATTERN =
  /lichess\.org\/[a-zA-Z0-9]{8}\/(white|black)/i

function detectPerspectiveFromUrl(url: string): "white" | "black" | null {
  const match = url.trim().match(LICHESS_PERSPECTIVE_PATTERN)
  if (!match) return null
  return match[1].toLowerCase() as "white" | "black"
}

function extractPlayerNames(pgn: string): { white: string; black: string } | null {
  const whiteMatch = pgn.match(/\[White\s+"(.+?)"\]/)
  const blackMatch = pgn.match(/\[Black\s+"(.+?)"\]/)
  if (!whiteMatch || !blackMatch) return null
  const white = whiteMatch[1].trim()
  const black = blackMatch[1].trim()
  if (!white || !black || white === "?" || black === "?") return null
  return { white, black }
}

function makeInitialStages(): ProcessingStage[] {
  return [
    { id: "parse", label: "Parsing your game...", status: "pending" },
    { id: "analyze", label: "Analyzing positions...", status: "pending" },
    { id: "detect", label: "Finding the story...", status: "pending" },
    { id: "narrate", label: "Writing the narration...", status: "pending" },
    { id: "create", label: "Creating your replay...", status: "pending" },
  ]
}

// --- Component ---

export const SenderPage = () => {
  // --- Invite code state ---
  const [inviteCode, setInviteCode] = useState<string | null>(() => {
    try {
      return localStorage.getItem(INVITE_CODE_KEY)
    } catch {
      return null
    }
  })
  const [inviteInput, setInviteInput] = useState("")
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteLoading, setInviteLoading] = useState(false)

  // --- Feedback banner state ---
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(false)

  // --- State ---
  const [pageState, setPageState] = useState<PageState>("input")
  const [pgnText, setPgnText] = useState("")
  const [pgnError, setPgnError] = useState<string | null>(null)
  const [nudge, setNudge] = useState<NudgeOption>(null)
  const [senderColor, setSenderColor] = useState<"white" | "black" | null>(null)
  const [playerNames, setPlayerNames] = useState<{ white: string; black: string } | null>(null)
  const [stages, setStages] = useState<ProcessingStage[]>(makeInitialStages)
  const [moveProgress, setMoveProgress] = useState<{ current: number; total: number } | null>(null)
  const [resultData, setResultData] = useState<ResultData | null>(null)
  const [errorData, setErrorData] = useState<ErrorData | null>(null)
  const [copied, setCopied] = useState(false)

  // Keep pipeline result around for retry (re-sends to server without re-running Stockfish)
  const pipelineResultRef = useRef<PipelineResult | null>(null)
  const stockfishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stockfishWarnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // --- Invite code verification ---
  const handleInviteSubmit = useCallback(async () => {
    const code = inviteInput.trim()
    if (!code) return

    setInviteError(null)
    setInviteLoading(true)

    try {
      const response = await fetch(`/api/verify-code?code=${encodeURIComponent(code)}`)
      const data = await response.json() as { valid: boolean; code: string | null }

      if (data.valid && data.code) {
        // Store in localStorage and unlock the form
        try {
          localStorage.setItem(INVITE_CODE_KEY, data.code)
        } catch {
          // localStorage unavailable — still let them use it for this session
        }
        setInviteCode(data.code)
        setInviteError(null)
      } else {
        setInviteError("That code doesn't look right. Check for typos, or DM @kibitzshare on X for help.")
      }
    } catch {
      setInviteError("Could not verify code. Check your connection and try again.")
    } finally {
      setInviteLoading(false)
    }
  }, [inviteInput])

  // --- Cleanup timers on unmount ---
  useEffect(() => {
    return () => {
      if (stockfishTimerRef.current) clearTimeout(stockfishTimerRef.current)
      if (stockfishWarnTimerRef.current) clearTimeout(stockfishWarnTimerRef.current)
    }
  }, [])

  // --- Stage updaters ---

  const updateStage = useCallback(
    (stageId: string, status: ProcessingStage["status"], detail?: string) => {
      setStages((prev) =>
        prev.map((s) => (s.id === stageId ? { ...s, status, detail } : s))
      )
    },
    []
  )

  const activateStage = useCallback(
    (stageId: string) => {
      setStages((prev) =>
        prev.map((s) => {
          if (s.id === stageId) return { ...s, status: "active" as const }
          if (s.status === "active") return { ...s, status: "complete" as const }
          return s
        })
      )
    },
    []
  )

  // --- Pipeline execution ---

  const handleSubmit = useCallback(async () => {
    // Check if input is a game URL — fetch PGN first
    let effectivePgn = pgnText

    if (looksLikeGameUrl(pgnText)) {
      setPgnError(null)
      setPageState("processing")
      setStages([
        { id: "import", label: "Importing game...", status: "active" },
        ...makeInitialStages(),
      ])
      setMoveProgress(null)
      pipelineResultRef.current = null

      const importResult = await fetchPgnFromLink(pgnText)
      if (!importResult.ok) {
        setPgnError(importResult.error)
        setPageState("input")
        return
      }

      effectivePgn = importResult.pgn
      setPgnText(effectivePgn)

      // Auto-detect perspective from Lichess URL suffix
      const perspective = detectPerspectiveFromUrl(pgnText)
      if (perspective) {
        setSenderColor(perspective)
      }

      // Mark import complete, continue to parse
      setStages((prev) =>
        prev.map((s) =>
          s.id === "import" ? { ...s, status: "complete" as const } : s
        )
      )
    }

    // Validate PGN
    const validation = validatePgn(effectivePgn)
    if (!validation.valid) {
      if (validation.error) setPgnError(validation.error)
      if (pageState !== "input") setPageState("input")
      return
    }

    if (pageState !== "processing") {
      setPgnError(null)
      setPageState("processing")
      setStages(makeInitialStages())
      setMoveProgress(null)
      pipelineResultRef.current = null
    }

    try {
      // Stage 1: Parse
      activateStage("parse")
      // Parse is instant -- handled inside runPipeline

      // Stage 2: Analyze (Stockfish)
      // We set up the Stockfish timeout warnings
      let stockfishTimedOut = false

      stockfishTimerRef.current = setTimeout(() => {
        updateStage(
          "analyze",
          "active",
          "Your device is taking a while to analyze this game. Hang tight..."
        )
      }, STOCKFISH_TIMEOUT_MS)

      stockfishWarnTimerRef.current = setTimeout(() => {
        updateStage(
          "analyze",
          "active",
          "Analysis is taking longer than expected. For faster results, try on a desktop browser."
        )
        stockfishTimedOut = true
      }, STOCKFISH_WARN_MS)

      const onProgress = (progress: StockfishProgress) => {
        if (progress.phase === "analyzing") {
          activateStage("analyze")
          setMoveProgress({ current: progress.currentMove, total: progress.totalMoves })
        }
      }

      // Run the client-side pipeline
      activateStage("parse")

      let result: PipelineResult
      try {
        result = await runPipeline(effectivePgn, { onProgress })
      } catch (err) {
        // Stockfish or parse failure
        if (stockfishTimerRef.current) clearTimeout(stockfishTimerRef.current)
        if (stockfishWarnTimerRef.current) clearTimeout(stockfishWarnTimerRef.current)

        const message = stockfishTimedOut
          ? "Analysis took too long. Try a shorter game or use a desktop browser."
          : err instanceof Error
            ? err.message
            : "Something went wrong during analysis."

        setErrorData({
          title: "Analysis failed",
          message,
          canRetry: true,
        })
        setPageState("error")
        return
      }

      // Clear Stockfish timers
      if (stockfishTimerRef.current) clearTimeout(stockfishTimerRef.current)
      if (stockfishWarnTimerRef.current) clearTimeout(stockfishWarnTimerRef.current)

      pipelineResultRef.current = result
      setMoveProgress(null)

      // Stage 3: Story detection (already done inside pipeline, just update UI)
      activateStage("detect")

      // Stage 4: Send to server
      activateStage("narrate")
      await sendToServer(result, effectivePgn)
    } catch (err) {
      console.error("Pipeline error:", err)
      setErrorData({
        title: "Something went wrong",
        message: "An unexpected error occurred. Please try again.",
        canRetry: true,
      })
      setPageState("error")
    }
  }, [pgnText, nudge, activateStage, updateStage])

  const sendToServer = useCallback(
    async (result: PipelineResult, pgnOverride?: string) => {
      activateStage("narrate")

      try {
        const response = await fetch("/api/narrate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "text/event-stream",
          },
          body: JSON.stringify({
            gameBrief: result.brief,
            pgn: pgnOverride ?? pgnText,
            moves: result.moves,
            nudge,
            senderColor,
            inviteCode,
          }),
        })

        // Non-streaming error responses (400, 429, dedup 200) return JSON
        const contentType = response.headers.get("Content-Type") ?? ""
        if (!contentType.includes("text/event-stream")) {
          const data: CreateReplayResponse = await response.json()

          if (!data.ok) {
            if (data.error === "invalid_invite_code") {
              // Code became invalid or was cleared — reset the gate
              setInviteCode(null)
              try {
                localStorage.removeItem(INVITE_CODE_KEY)
              } catch {
                // localStorage unavailable
              }
              setErrorData({
                title: "Invalid invite code",
                message: "Your invite code is no longer valid. Please enter a new one.",
                canRetry: false,
              })
            } else if (data.error === "rate_limited") {
              setErrorData({
                title: "Too many requests",
                message: "You've created too many replays recently. Try again in a few minutes.",
                canRetry: false,
              })
            } else if (data.error === "narration_failed") {
              setErrorData({
                title: "Narration failed",
                message: "The story generation failed. Your game has been analyzed -- try again.",
                canRetry: true,
              })
            } else {
              setErrorData({
                title: "Server error",
                message: data.message || "Something went wrong. Please try again.",
                canRetry: true,
              })
            }
            setPageState("error")
            return
          }

          // Non-streaming success (dedup hit returns JSON directly)
          handleNarrateSuccess(data.id, data.url, result)
          return
        }

        // --- Streaming SSE response ---
        const reader = response.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ""

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // SSE events are separated by double newlines
          const events = buffer.split("\n\n")
          buffer = events.pop()! // Keep incomplete last chunk

          for (const eventStr of events) {
            const dataLine = eventStr
              .split("\n")
              .find((l) => l.startsWith("data: "))
            if (!dataLine) continue

            try {
              const event = JSON.parse(dataLine.slice(6)) as NarrateStreamEvent

              switch (event.type) {
                case "progress":
                  updateStage(event.stage, "active", event.detail)
                  break
                case "complete":
                  handleNarrateSuccess(event.id, event.url, result)
                  return
                case "error":
                  setErrorData({
                    title: "Narration failed",
                    message: event.message || "Something went wrong. Please try again.",
                    canRetry: true,
                  })
                  setPageState("error")
                  return
              }
            } catch {
              // Skip malformed SSE events
            }
          }
        }
      } catch {
        // Network error
        setErrorData({
          title: "Connection failed",
          message: "Could not reach the server. Check your internet connection and try again.",
          canRetry: true,
        })
        setPageState("error")
      }
    },
    [pgnText, nudge, senderColor, inviteCode, activateStage, updateStage]
  )

  const handleNarrateSuccess = useCallback(
    async (id: string, url: string, result: PipelineResult) => {
      activateStage("create")

      // Brief delay for the "Creating your replay..." stage to show
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Complete the final stage
      setStages((prev) =>
        prev.map((s) => ({ ...s, status: "complete" as const }))
      )

      // Use current origin for the URL (handles dev mode port differences)
      const replayUrl = `${window.location.origin}/r/${id}`

      setResultData({
        id,
        url: replayUrl,
        headline: result.brief.gameMetadata.white + " vs " + result.brief.gameMetadata.black,
        hook: `A ${result.brief.gameArc.replace(/_/g, " ")} in ${result.brief.gameMetadata.totalMoves} moves`,
        white: result.brief.gameMetadata.white,
        black: result.brief.gameMetadata.black,
        result: result.brief.gameMetadata.result,
      })

      // Track replay count and show feedback banner
      try {
        const currentCount = parseInt(localStorage.getItem(REPLAY_COUNT_KEY) ?? "0", 10)
        const newCount = currentCount + 1
        localStorage.setItem(REPLAY_COUNT_KEY, String(newCount))

        const dismissed = localStorage.getItem(FEEDBACK_DISMISSED_KEY) === "true"
        if (newCount >= FEEDBACK_THRESHOLD && !dismissed && FEEDBACK_FORM_URL) {
          setShowFeedbackBanner(true)
        }
      } catch {
        // localStorage unavailable
      }

      // Auto-copy URL
      try {
        await navigator.clipboard.writeText(url)
        setCopied(true)
        setTimeout(() => setCopied(false), 3000)
      } catch {
        // Clipboard API not available -- user can click copy
      }

      setPageState("result")
    },
    [activateStage]
  )

  // --- Retry handler ---

  const handleRetry = useCallback(() => {
    if (pipelineResultRef.current) {
      // We already have the pipeline result -- just retry the server call
      setPageState("processing")
      setStages((prev) =>
        prev.map((s) => {
          if (s.id === "narrate" || s.id === "create") {
            return { ...s, status: "pending" as const, detail: undefined }
          }
          return { ...s, status: "complete" as const, detail: undefined }
        })
      )
      sendToServer(pipelineResultRef.current)
    } else {
      // Full retry
      handleSubmit()
    }
  }, [handleSubmit, sendToServer])

  // --- Feedback dismiss handler ---

  const handleDismissFeedback = useCallback(() => {
    setShowFeedbackBanner(false)
    try {
      localStorage.setItem(FEEDBACK_DISMISSED_KEY, "true")
    } catch {
      // localStorage unavailable
    }
  }, [])

  // --- Reset handler ---

  const handleReset = useCallback(() => {
    setPageState("input")
    setPgnText("")
    setPgnError(null)
    setNudge(null)
    setSenderColor(null)
    setPlayerNames(null)
    setStages(makeInitialStages())
    setMoveProgress(null)
    setResultData(null)
    setErrorData(null)
    setCopied(false)
    setShowFeedbackBanner(false)
    pipelineResultRef.current = null
  }, [])

  // --- Extract player names from PGN ---

  useEffect(() => {
    const names = extractPlayerNames(pgnText)
    setPlayerNames(names)
  }, [pgnText])

  // --- Copy handler ---

  const handleCopy = useCallback(async () => {
    if (!resultData) return
    try {
      await navigator.clipboard.writeText(resultData.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select the text in the input
    }
  }, [resultData])

  // --- PGN change handler ---

  const handlePgnChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      if (value.length > MAX_PGN_LENGTH) {
        setPgnError(`PGN is too long (${value.length.toLocaleString()} / ${MAX_PGN_LENGTH.toLocaleString()} characters). Paste a single game only.`)
        return
      }
      setPgnText(value)
      if (pgnError) setPgnError(null)
    },
    [pgnError]
  )

  // --- Render ---

  const isOverLimit = pgnText.length > MAX_PGN_LENGTH
  const isSubmitDisabled = pgnText.trim().length === 0 || isOverLimit

  return (
    <main className={styles.container}>
      <div className={styles.content}>
        {pageState === "input" && !inviteCode && (
          <div className={styles.stateWrapper} key="invite" data-state="input">
            <div className={styles.brand}>
              <div className={styles.brandIcon}>K</div>
              <div className={styles.brandName}>
                Kibitz<span className={styles.brandAccent}>Share</span>
              </div>
            </div>

            <h1 className={styles.heading}>
              Turn your chess game<br />into a <em className={styles.headingAccent}>story</em>
            </h1>
            <p className={styles.subheading}>
              KibitzShare is in closed beta
            </p>

            <div className={styles.inviteGate}>
              <p className={styles.inviteLabel}>Enter your invite code to get started</p>
              <div className={styles.inviteInputRow}>
                <input
                  className={`${styles.inviteInput} ${inviteError ? styles.inviteInputError : ""}`}
                  type="text"
                  value={inviteInput}
                  onChange={(e) => {
                    setInviteInput(e.target.value)
                    if (inviteError) setInviteError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleInviteSubmit()
                  }}
                  placeholder="Enter code"
                  autoFocus
                  aria-label="Invite code"
                  aria-invalid={!!inviteError}
                />
                <button
                  className={styles.inviteButton}
                  onClick={handleInviteSubmit}
                  disabled={inviteInput.trim().length === 0 || inviteLoading}
                  type="button"
                >
                  {inviteLoading ? "..." : "Enter"}
                </button>
              </div>
              {inviteError && (
                <p className={styles.inviteError} role="alert">{inviteError}</p>
              )}
              <p className={styles.inviteHelp}>
                Don&apos;t have a code? DM{" "}
                <a href="https://x.com/kibitzshare" target="_blank" rel="noopener noreferrer">
                  <strong>@kibitzshare</strong>
                </a>{" "}
                on X
              </p>
            </div>
          </div>
        )}

        {pageState === "input" && inviteCode && (
          <div className={styles.stateWrapper} key="input" data-state="input">
            <div className={styles.brand}>
              <div className={styles.brandIcon}>K</div>
              <div className={styles.brandName}>
                Kibitz<span className={styles.brandAccent}>Share</span>
              </div>
            </div>

            <h1 className={styles.heading}>
              Turn your chess game<br />into a <em className={styles.headingAccent}>story</em>
            </h1>
            <p className={styles.subheading}>
              Paste a PGN and get a shareable replay in seconds
            </p>

            <div className={styles.formCard}>
              <div className={styles.textareaWrapper}>
                <label className={styles.formLabel} htmlFor="pgn-input">
                  Game Notation
                </label>
                <textarea
                  id="pgn-input"
                  className={`${styles.textarea} ${pgnError ? styles.textareaError : ""}`}
                  value={pgnText}
                  onChange={handlePgnChange}
                  placeholder={PGN_PLACEHOLDER}
                  maxLength={MAX_PGN_LENGTH}
                  aria-label="Paste your PGN here"
                  aria-invalid={!!pgnError}
                  aria-describedby={pgnError ? "pgn-error" : undefined}
                />
                <div className={styles.textareaFooter}>
                  {pgnError ? (
                    <p className={styles.errorMessage} id="pgn-error" role="alert">
                      {pgnError}
                    </p>
                  ) : (
                    <span />
                  )}
                  {pgnText.length > 0 && (
                    <span className={`${styles.charCount} ${isOverLimit ? styles.charCountOver : ""}`}>
                      {pgnText.length.toLocaleString()} / {MAX_PGN_LENGTH.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Highlight nudge — always visible */}
              <div className={styles.nudgeSection}>
                <p className={styles.nudgeLabel}>What do you want to highlight?</p>
                <div className={styles.nudgeChips} role="radiogroup" aria-label="Story hint">
                  {NUDGE_OPTIONS.map((option) => (
                    <button
                      key={option.value ?? "auto"}
                      className={`${styles.nudgeChip} ${
                        nudge === option.value ? styles.nudgeChipSelected : ""
                      }`}
                      onClick={() => setNudge(option.value)}
                      type="button"
                      role="radio"
                      aria-checked={nudge === option.value}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Player selector — visible when player names are detected */}
              {playerNames && (
                <div className={styles.nudgeSection}>
                  <p className={styles.nudgeLabel}>Which player are you?</p>
                  <div className={styles.nudgeChips} role="radiogroup" aria-label="Player selection">
                    <button
                      className={`${styles.nudgeChip} ${
                        senderColor === "white" ? styles.nudgeChipSelected : ""
                      }`}
                      onClick={() => setSenderColor("white")}
                      type="button"
                      role="radio"
                      aria-checked={senderColor === "white"}
                    >
                      {playerNames.white} (White)
                    </button>
                    <button
                      className={`${styles.nudgeChip} ${
                        senderColor === "black" ? styles.nudgeChipSelected : ""
                      }`}
                      onClick={() => setSenderColor("black")}
                      type="button"
                      role="radio"
                      aria-checked={senderColor === "black"}
                    >
                      {playerNames.black} (Black)
                    </button>
                    <button
                      className={`${styles.nudgeChip} ${
                        senderColor === null ? styles.nudgeChipSelected : ""
                      }`}
                      onClick={() => setSenderColor(null)}
                      type="button"
                      role="radio"
                      aria-checked={senderColor === null}
                    >
                      Neither
                    </button>
                  </div>
                </div>
              )}

              <div className={styles.formDivider} />

              <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                type="button"
              >
                Create Replay
              </button>

              <p className={styles.keyboardHint}>
                <kbd className={styles.kbd}>&#8984;</kbd> +{" "}
                <kbd className={styles.kbd}>Enter</kbd> to submit
              </p>
            </div>

            <details className={styles.pgnHelp}>
              <summary className={styles.pgnHelpToggle}>
                <span className={styles.helpIcon}>?</span>
                How do I get my game?
              </summary>
              <div className={styles.pgnHelpContent}>
                <p>
                  <strong>Easiest:</strong> paste a game link from Chess.com or
                  Lichess and we&apos;ll import it automatically.
                </p>
                <ul className={styles.pgnHelpList}>
                  <li>
                    <strong>Chess.com</strong> — Open a completed game, tap
                    Share, and copy the link
                  </li>
                  <li>
                    <strong>Lichess</strong> — Open a game and copy the URL from
                    your browser
                  </li>
                </ul>
                <p>
                  You can also paste a PGN directly if you have one.
                </p>
              </div>
            </details>
          </div>
        )}

        {pageState === "processing" && (
          <div className={styles.stateWrapper} key="processing" data-state="processing">
            <div className={styles.processingContainer}>
              <div className={styles.progressStages}>
                {stages.map((stage) => (
                  <div
                    key={stage.id}
                    className={`${styles.progressStage} ${
                      stage.status === "active" ? styles.progressStageActive : ""
                    } ${stage.status === "complete" ? styles.progressStageComplete : ""} ${
                      stage.status === "error" ? styles.progressStageError : ""
                    }`}
                  >
                    <span className={styles.stageIcon}>
                      {stage.status === "active" && <span className={styles.spinner} />}
                      {stage.status === "complete" && (
                        <span className={styles.checkmark}>&#10003;</span>
                      )}
                      {stage.status === "pending" && (
                        <span style={{ opacity: 0.3 }}>&#9679;</span>
                      )}
                      {stage.status === "error" && (
                        <span>&#10007;</span>
                      )}
                    </span>
                    <span className={styles.stageLabel}>
                      {stage.label}
                      {stage.detail && (
                        <span
                          style={{
                            display: "block",
                            fontSize: "var(--font-size-sm)",
                            color: "var(--color-text-muted)",
                            marginTop: "var(--space-1)",
                          }}
                        >
                          {stage.detail}
                        </span>
                      )}
                    </span>
                    {stage.id === "analyze" &&
                      stage.status === "active" &&
                      moveProgress && (
                        <span className={styles.moveCounter}>
                          {moveProgress.current}/{moveProgress.total}
                        </span>
                      )}
                  </div>
                ))}
              </div>

              {/* Decorative board animation placeholder */}
              <ProcessingBoardAnimation
                pgn={pgnText}
                isActive={pageState === "processing"}
              />
            </div>
          </div>
        )}

        {pageState === "result" && resultData && (
          <div className={styles.stateWrapper} key="result" data-state="result">
            <div className={styles.resultContainer}>
              <h2 className={styles.resultHeading}>Your replay is ready!</h2>

              <div className={styles.urlContainer}>
                <input
                  className={styles.urlInput}
                  value={resultData.url}
                  readOnly
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  aria-label="Shareable replay URL"
                />
                <button
                  className={`${styles.copyButton} ${copied ? styles.copyButtonCopied : ""}`}
                  onClick={handleCopy}
                  type="button"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>

              <a
                className={styles.previewCard}
                href={resultData.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Preview your replay"
              >
                <div className={styles.previewContent}>
                  <p className={styles.previewHeadline}>{resultData.headline}</p>
                  <p className={styles.previewHook}>{resultData.hook}</p>
                  <p className={styles.previewMeta}>
                    {resultData.white} vs {resultData.black} &middot; {resultData.result}
                  </p>
                </div>
              </a>

              {showFeedbackBanner && (
                <div className={styles.feedbackBanner}>
                  <div className={styles.feedbackBannerContent}>
                    <p className={styles.feedbackBannerHeading}>
                      Enjoying KibitzShare?
                    </p>
                    <p className={styles.feedbackBannerText}>
                      You&apos;re one of our first beta testers. A 2-minute survey helps us build what matters.
                    </p>
                    <a
                      className={styles.feedbackBannerLink}
                      href={FEEDBACK_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Share feedback
                    </a>
                  </div>
                  <button
                    className={styles.feedbackBannerClose}
                    onClick={handleDismissFeedback}
                    type="button"
                    aria-label="Dismiss feedback banner"
                  >
                    &times;
                  </button>
                </div>
              )}

              <div className={styles.resultActions}>
                <button
                  className={styles.createAnotherButton}
                  onClick={handleReset}
                  type="button"
                >
                  Create another replay
                </button>
              </div>
            </div>
          </div>
        )}

        {pageState === "error" && errorData && (
          <div className={styles.stateWrapper} key="error" data-state="error">
            <div className={styles.errorContainer}>
              <h2 className={styles.errorTitle}>{errorData.title}</h2>
              <p className={styles.errorDescription}>{errorData.message}</p>
              {errorData.canRetry && (
                <button
                  className={styles.retryButton}
                  onClick={handleRetry}
                  type="button"
                >
                  Try again
                </button>
              )}
              <button
                className={styles.backButton}
                onClick={handleReset}
                type="button"
              >
                Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}

// --- Processing Board Animation (Task 4.8) ---

interface ProcessingBoardProps {
  pgn: string
  isActive: boolean
}

const ProcessingBoardAnimation = ({ pgn, isActive }: ProcessingBoardProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animFrameRef = useRef<number | null>(null)
  const moveIndexRef = useRef(0)

  useEffect(() => {
    if (!isActive || !canvasRef.current) return

    let moves: { from: string; to: string; fen: string }[] = []

    try {
      const chess = new Chess()
      chess.loadPgn(pgn)
      const history = chess.history({ verbose: true })

      // Replay to get FENs
      chess.reset()
      moves = history.map((m) => {
        chess.move(m.san)
        return { from: m.from, to: m.to, fen: chess.fen() }
      })
    } catch {
      return
    }

    if (moves.length === 0) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const size = canvas.width
    const sq = size / 8

    function drawBoard(fen: string) {
      if (!ctx) return
      // Draw squares
      for (let r = 0; r < 8; r++) {
        for (let c = 0; c < 8; c++) {
          ctx.fillStyle = (r + c) % 2 === 0 ? "#f0d9b5" : "#b58863"
          ctx.fillRect(c * sq, r * sq, sq, sq)
        }
      }

      // Draw pieces from FEN
      const placement = fen.split(" ")[0]
      const rows = placement.split("/")

      for (let r = 0; r < rows.length; r++) {
        let c = 0
        for (const ch of rows[r]) {
          if (/\d/.test(ch)) {
            c += parseInt(ch, 10)
          } else {
            drawPiece(ctx, ch, c * sq, r * sq, sq)
            c++
          }
        }
      }
    }

    function drawPiece(
      ctx: CanvasRenderingContext2D,
      piece: string,
      x: number,
      y: number,
      size: number
    ) {
      const pieceMap: Record<string, string> = {
        K: "\u2654",
        Q: "\u2655",
        R: "\u2656",
        B: "\u2657",
        N: "\u2658",
        P: "\u2659",
        k: "\u265A",
        q: "\u265B",
        r: "\u265C",
        b: "\u265D",
        n: "\u265E",
        p: "\u265F",
      }

      const symbol = pieceMap[piece]
      if (!symbol) return

      ctx.font = `${size * 0.75}px serif`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillStyle = /[A-Z]/.test(piece) ? "#ffffff" : "#1C1917"
      ctx.strokeStyle = /[A-Z]/.test(piece) ? "#1C1917" : "#ffffff"
      ctx.lineWidth = 1
      ctx.strokeText(symbol, x + size / 2, y + size / 2)
      ctx.fillText(symbol, x + size / 2, y + size / 2)
    }

    // Draw initial position
    const startFen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
    drawBoard(startFen)

    // Animate through moves using rAF (pauses when tab is backgrounded)
    moveIndexRef.current = 0
    let lastFrameTime = 0
    const FRAME_INTERVAL = 80 // ms per move step

    const animate = (timestamp: number) => {
      if (timestamp - lastFrameTime >= FRAME_INTERVAL) {
        lastFrameTime = timestamp

        if (moveIndexRef.current >= moves.length) {
          moveIndexRef.current = 0
          drawBoard(startFen)
        } else {
          drawBoard(moves[moveIndexRef.current].fen)
          moveIndexRef.current++
        }
      }

      animFrameRef.current = requestAnimationFrame(animate)
    }

    animFrameRef.current = requestAnimationFrame(animate)

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = null
      }
    }
  }, [pgn, isActive])

  return (
    <canvas
      ref={canvasRef}
      className={styles.processingBoard}
      width={280}
      height={280}
      aria-hidden="true"
    />
  )
}
